import {
	Avatar,
	Badge,
	Card,
	Group,
	SimpleGrid,
	Stack,
	Text,
} from "@mantine/core";
import { useSnapshot } from "valtio";
import { assistantLogTexts } from "@/locales/assistant-log";
import { assistantLogDetailTexts } from "@/locales/assistant-log-detail";
import { i18nStore } from "@/store/i18n";
import type {
	AssistantLogTotalsDto,
	AssistantLogUserProfileDto,
} from "@/types/ai-assistant-log";
import {
	formatLogCost,
	formatLogCount,
	formatLogTokens,
	logDisplayName,
	logInitials,
} from "./log-format.logic";

/** Kartu profil lapis 2: identitas, lencana izin, dan lima angka ringkas. */
export function LogUserProfile({
	profile,
	totals,
}: {
	profile: AssistantLogUserProfileDto;
	totals: AssistantLogTotalsDto;
}) {
	const { lang: uiLang } = useSnapshot(i18nStore);
	const lang = uiLang === "en" ? "en" : "id";
	const t = assistantLogTexts[lang];
	const td = assistantLogDetailTexts[lang];

	const numbers = [
		{
			label: t.statConversations,
			value: formatLogCount(totals.conversations, lang),
		},
		{ label: t.statTurns, value: formatLogCount(totals.turns, lang) },
		{
			label: t.statTokens,
			value: formatLogTokens(totals.inputTokens, totals.outputTokens, lang),
		},
		{ label: t.statCost, value: formatLogCost(totals.cost, t, lang) },
		{ label: t.colErrors, value: formatLogCount(totals.errors, lang) },
	];

	return (
		<Card withBorder radius="md" p="lg" data-log-user-profile>
			<Stack gap="md">
				<Group gap="md" wrap="nowrap">
					<Avatar color="orange" radius="xl" size="lg">
						{logInitials(profile.name, profile.email)}
					</Avatar>
					<Stack gap={2} flex={1}>
						<Text fw={700}>{logDisplayName(profile.name, profile.email)}</Text>
						<Text size="sm" c="dimmed">
							{profile.email}
						</Text>
						<Group gap="xs" mt={4}>
							<Badge
								color={profile.emailVerified ? "green" : "gray"}
								variant="light"
								size="sm"
							>
								{profile.emailVerified
									? td.profileVerified
									: td.profileUnverified}
							</Badge>
							<Badge
								color={profile.canUseAssistant ? "orange" : "gray"}
								variant="light"
								size="sm"
								ff="monospace"
							>
								{profile.canUseAssistant
									? td.profileAssistantAllowed
									: td.profileAssistantBlocked}
							</Badge>
							{profile.role && (
								<Badge color="blue" variant="light" size="sm" tt="uppercase">
									{profile.role}
								</Badge>
							)}
						</Group>
					</Stack>
				</Group>

				<SimpleGrid cols={{ base: 2, md: 5 }} spacing="sm">
					{numbers.map((item) => (
						<Stack key={item.label} gap={0}>
							<Text size="xs" c="dimmed" tt="uppercase" fw={600}>
								{item.label}
							</Text>
							<Text fw={700}>{item.value}</Text>
						</Stack>
					))}
				</SimpleGrid>
			</Stack>
		</Card>
	);
}
